/**
 * Meal detail route
 * Display single meal with tags and popularity
 */

import { formatDateFull } from '@/lib/utils/date';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMeal, useDeleteMeal } from '@/hooks/useMeals';
import {
  useExclusionsByMeal,
  useCreateMealExclusion,
  useDeleteMealExclusion,
} from '@/hooks/useConstraints';
import { useUsers } from '@/hooks/useUsers';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { requireAuth } from '@/lib/auth/routeGuards';
import { capitalize } from '@/lib/utils/text';
import { useToast } from '@/components/ui/ToastProvider';
import type { MealExclusion, User } from '@/types/api';

export const Route = createFileRoute('/meals/$mealId')({
  beforeLoad: requireAuth,
  component: MealDetailComponent,
});

interface ExcludedMemberItemProps {
  exclusion: MealExclusion;
  onDelete: () => void;
  isDeleting: boolean;
}

function ExcludedMemberItem({ exclusion, onDelete, isDeleting }: ExcludedMemberItemProps) {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <div className="flex-1">
        <p className="font-medium text-gray-900">{exclusion.userName}</p>
        {exclusion.reason && (
          <p className="text-sm text-gray-500">{exclusion.reason}</p>
        )}
      </div>
      <button
        onClick={onDelete}
        disabled={isDeleting}
        className="ml-3 text-gray-400 hover:text-red-500 disabled:opacity-50"
        title={t('common.remove')}
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

interface AddExcludedMemberFormProps {
  mealId: number;
  existingExclusions: MealExclusion[];
  onSuccess: () => void;
}

function AddExcludedMemberForm({ mealId, existingExclusions, onSuccess }: AddExcludedMemberFormProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [reason, setReason] = useState('');
  const createExclusion = useCreateMealExclusion();
  const { data: usersData } = useUsers();

  const users = usersData?.users || [];
  const excludedUserIds = new Set(existingExclusions.map((e) => e.userId));
  const availableUsers = users.filter((u) => !excludedUserIds.has(u.id));
  const filteredUsers = searchQuery
    ? availableUsers.filter((u) =>
        u.name.toLowerCase().includes(searchQuery.toLowerCase())
      ).slice(0, 5)
    : availableUsers.slice(0, 5);

  const handleSubmit = async () => {
    if (!selectedUser) return;

    try {
      await createExclusion.mutateAsync({
        userId: selectedUser.id,
        mealId,
        reason: reason || undefined,
      });
      toast.success(t('meals.memberExcluded'));
      setSelectedUser(null);
      setReason('');
      setSearchQuery('');
      setIsOpen(false);
      onSuccess();
    } catch {
      toast.error(t('meals.errorAddingExclusion'));
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100"
      >
        + {t('meals.addExcludedMember')}
      </button>
    );
  }

  return (
    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
      <div className="space-y-3">
        {!selectedUser ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('meals.selectMember')}
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('meals.searchMembers')}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
            />
            {filteredUsers.length > 0 && (
              <div className="mt-2 border border-gray-200 rounded-md bg-white max-h-40 overflow-y-auto">
                {filteredUsers.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => {
                      setSelectedUser(user);
                      setSearchQuery('');
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                  >
                    {user.name}
                  </button>
                ))}
              </div>
            )}
            {availableUsers.length === 0 && (
              <p className="mt-2 text-sm text-gray-500">
                {t('meals.noExcludedMembers')}
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between p-2 bg-white rounded border border-gray-200">
              <span className="font-medium text-gray-900">{selectedUser.name}</span>
              <button
                type="button"
                onClick={() => setSelectedUser(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('meals.exclusionReason')}
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('meals.exclusionReasonPlaceholder')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={createExclusion.isPending}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {createExclusion.isPending ? t('app.saving') : t('app.save')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  setSelectedUser(null);
                  setReason('');
                }}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {t('app.cancel')}
              </button>
            </div>
          </>
        )}

        {!selectedUser && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {t('app.cancel')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function MealDetailComponent() {
  const { t } = useTranslation();
  const { mealId } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToast();
  // Get user from route context - guaranteed non-null by requireAuth
  const { user } = Route.useRouteContext();
  const mealIdNum = parseInt(mealId, 10);
  const isValidMealId = Number.isFinite(mealIdNum);
  const safeMealId = isValidMealId ? mealIdNum : -1;
  const { data: meal, isLoading, error } = useMeal(safeMealId);
  const deleteMealMutation = useDeleteMeal();
  const { data: exclusions = [], refetch: refetchExclusions } = useExclusionsByMeal(safeMealId);
  const deleteExclusion = useDeleteMealExclusion();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingExclusionId, setDeletingExclusionId] = useState<number | null>(null);

  const handleDeleteExclusion = async (exclusionId: number) => {
    setDeletingExclusionId(exclusionId);
    try {
      await deleteExclusion.mutateAsync(exclusionId);
      toast.success(t('meals.exclusionRemoved'));
      refetchExclusions();
    } catch {
      toast.error(t('meals.errorRemovingExclusion'));
    } finally {
      setDeletingExclusionId(null);
    }
  };

  // Invalid meal id in URL (e.g. /meals/abc)
  if (!isValidMealId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link
            to="/meals"
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 mb-6"
          >
            <svg
              className="h-4 w-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            {t('meals.backToMeals')}
          </Link>

          <div className="rounded-md bg-red-50 p-4" role="alert">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {t('meals.invalidMealId')}
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  {t('meals.invalidMealIdDetail')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-32 mb-6" />
            <div className="bg-white rounded-lg shadow-md p-8">
              <div className="h-10 bg-gray-200 rounded w-3/4 mb-6" />
              <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" />
              <div className="flex gap-2">
                <div className="h-6 bg-gray-200 rounded w-20" />
                <div className="h-6 bg-gray-200 rounded w-24" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !meal) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link
            to="/meals"
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 mb-6"
          >
            <svg
              className="h-4 w-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            {t('meals.backToMeals')}
          </Link>

          <div className="rounded-md bg-red-50 p-4" role="alert">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {t('meals.errorLoadingMeal')}
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  {error instanceof Error
                    ? error.message
                    : t('meals.mealNotFound')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const visibleTags = meal.tags.filter((tag) => !tag.isHidden);
  const isOrganizer = user.isOrganizer;

  const handleDelete = async () => {
    try {
      await deleteMealMutation.mutateAsync(mealIdNum);
      navigate({ to: '/meals' });
    } catch (error) {
      console.error('Failed to delete meal:', error);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate({ to: '/meals' })}
          className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 mb-6"
        >
          <svg
            className="h-4 w-4 mr-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {t('meals.backToMeals')}
        </button>

        {/* Delete error message */}
        {deleteMealMutation.isError && (
          <div className="rounded-md bg-red-50 p-4 mb-6" role="alert">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{t('meals.errorDeletingMeal')}</h3>
                <div className="mt-2 text-sm text-red-700">
                  {deleteMealMutation.error instanceof Error
                    ? deleteMealMutation.error.message
                    : t('meals.deleteFailed')}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Meal details card */}
        <div className="bg-white rounded-lg shadow-md p-8">
          {/* Header with actions */}
          <div className="flex items-start justify-between mb-6">
            <h1 className="text-3xl font-bold text-gray-900 flex-1">{capitalize(meal.name)}</h1>

            {/* Organizer actions */}
            {isOrganizer && (
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => navigate({ to: `/meals/${mealId}/edit` })}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                  aria-label={t('meals.editMeal')}
                >
                  <svg
                    className="h-4 w-4 mr-1.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  {t('buttons.edit')}
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="inline-flex items-center px-3 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                  aria-label={t('meals.deleteMeal')}
                >
                  <svg
                    className="h-4 w-4 mr-1.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  {t('buttons.delete')}
                </button>
              </div>
            )}
          </div>

          {/* Tags section */}
          <div className="mb-6">
            <h2 className="text-sm font-medium text-gray-700 mb-2">{t('meals.tags')}</h2>
            {visibleTags.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {visibleTags.map((tag) => (
                  <span
                    key={tag.id}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800"
                  >
                    {capitalize(tag.tag)}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">{t('meals.noTagsAvailable')}</p>
            )}
          </div>

          {/* Excluded members section - organizers only */}
          {isOrganizer && (
            <div className="mb-6 border-t border-gray-200 pt-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-medium text-gray-700">
                  {t('meals.excludedMembers')}
                </h2>
                <AddExcludedMemberForm
                  mealId={mealIdNum}
                  existingExclusions={exclusions}
                  onSuccess={() => refetchExclusions()}
                />
              </div>

              {exclusions.length === 0 ? (
                <p className="text-sm text-gray-500">{t('meals.noExcludedMembers')}</p>
              ) : (
                <div className="space-y-2">
                  {exclusions.map((exclusion) => (
                    <ExcludedMemberItem
                      key={exclusion.id}
                      exclusion={exclusion}
                      onDelete={() => handleDeleteExclusion(exclusion.id)}
                      isDeleting={deletingExclusionId === exclusion.id}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Metadata */}
          <div className="border-t border-gray-200 pt-4">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="font-medium text-gray-700">{t('meals.created')}</dt>
                <dd className="text-gray-600">
                  {formatDateFull(new Date(meal.createdAt))}
                </dd>
              </div>
              <div>
                <dt className="font-medium text-gray-700">
                  {t('meals.repeatInterval')}
                </dt>
                <dd className="text-gray-600">
                  {t('meals.repeatIntervalValue', {
                    count: meal.repeatIntervalWeeks,
                  })}
                </dd>
              </div>
              <div>
                <dt className="font-medium text-gray-700">{t('meals.mealId')}</dt>
                <dd className="text-gray-600">{meal.id}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title={t('meals.deleteMealTitle')}
        message={
          <div className="text-sm text-gray-500">
            <p>{t('meals.deleteMealConfirm', { name: meal.name })}</p>
            <p className="mt-2 font-medium text-red-600">
              {t('meals.deleteMealWarning')}
            </p>
          </div>
        }
        confirmText={t('meals.deleteMeal')}
        cancelText={t('buttons.cancel')}
        variant="danger"
        isLoading={deleteMealMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
}
